import { withRetry } from '@/utils/withRetry';
import { graphErrorWithDetail } from './graph-request-error';
import { GRAPH_RETRY_OPTIONS } from './graph-retry';
import { describeTokenClaims } from './graph-token-claims';
import { acquireMailToken } from './mail-token';
import { uploadInChunks } from './upload-in-chunks';

// Same host and version the drive service talks to; kept local rather than shared because it is one
// string and neither service should have to import the other to say it.
const GRAPH_BASE_URL = 'https://graph.microsoft.com/v1.0';

export const INLINE_ATTACHMENT_MAX_BYTES = 3 * 1024 * 1024;

// Graph requires chunk sizes to be a multiple of 320 KiB. Same value the drive upload uses, for the
// same reason: it keeps request count sane for multi-MB photos and CAD drawings.
const ATTACHMENT_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

export interface MailDraft {
  id: string;
  webLink: string;
}

// The unified Outlook host, hardcoded rather than taken from the webLink's own origin: the
// origin-following `/mail/deeplink/compose/` form did not reliably open the compose surface.
export const toComposeWebLink = (
  webLink: string,
  messageId: string,
): string => {
  try {
    const source = new URL(webLink);
    const itemId =
      /[?&]ItemID=([^&]+)/i.exec(source.search)?.[1] ??
      encodeURIComponent(messageId);

    return `https://outlook.cloud.microsoft/mail/compose/${itemId}`;
  } catch {
    return webLink;
  }
};

interface GraphMessage {
  id?: string;
  webLink?: string;
}

interface GraphUploadSession {
  uploadUrl?: string;
}

/**
 * Encodes a Blob as base64 for an inline `fileAttachment`.
 *
 * Emphatically NOT `btoa(String.fromCharCode(...new Uint8Array(await blob.arrayBuffer())))`. That
 * spread throws `RangeError: Maximum call stack size exceeded` somewhere around 100–120 KB depending on
 * the engine — i.e. for very nearly every file that takes the inline branch at all. Chunking the spread
 * fixes the RangeError but then realises the bytes twice, once as a typed array and once as a JS
 * string, for a blob already near the memory-sensitive size ceiling.
 *
 * readAsDataURL has the platform do the encoding in one pass instead, leaving only the
 * `data:<mime>;base64,` prefix to strip. It is also the only option that works under jsdom, whose Blob
 * implements neither `arrayBuffer()` nor `text()`.
 *
 * Runs only for blobs at or under INLINE_ATTACHMENT_MAX_BYTES. Everything larger is sliced lazily by
 * uploadInChunks and never fully realised in memory at all.
 */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read attachment bytes'));
    reader.onload = () => {
      const result = String(reader.result);
      // A data URL always carries this separator, even for a zero-length blob
      // ("data:application/octet-stream;base64,").
      const separator = result.indexOf(',');
      resolve(separator === -1 ? '' : result.slice(separator + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Logs what the rejected token actually was, on a 401 only.
 *
 * A 401 from Graph means the BEARER was refused, and the two causes need opposite fixes: a non-Graph
 * `aud` is the wrong resource (no permission grant can help), while a Graph `aud` with `Mail.ReadWrite`
 * missing from `scp` means the delegated permission is not granted on the registration named by `appid`
 * — which, with two app registrations across two dev tenants here, is most often a grant applied to the
 * wrong one. Only on 401, and only these four claims: never the token.
 */
const logTokenOn401 = (response: Response, token: string): void => {
  if (response.status !== 401) return;

  /*
   * WWW-Authenticate is what separates the last two possibilities once `aud` and `scp` both check out.
   *
   * A Conditional Access / continuous-access-evaluation step-up arrives as a 401 carrying
   * `error="insufficient_claims"` and a base64 `claims=` payload — the token IS valid and DOES hold the
   * scope, which is precisely why nothing about the app registration explains it. The fix for that is a
   * claims challenge: re-acquire interactively passing those claims through, not any change to
   * permissions.
   *
   * May read as (none) even when present: WWW-Authenticate is not CORS-safelisted, so it is visible only
   * because Graph lists it in Access-Control-Expose-Headers. Worth reading anyway — when it is there it
   * is decisive, and its absence is itself informative.
   */
  const challenge = response.headers?.get?.('WWW-Authenticate') ?? '(none)';

  console.error(
    'Graph rejected the mail token —',
    describeTokenClaims(token),
    '(expected aud=https://graph.microsoft.com with Mail.ReadWrite in scp)',
    `\n  WWW-Authenticate: ${challenge}`,
  );
};

/**
 * Microsoft Graph mail operations, for the file manager's "Skapa outlook mail" tool.
 *
 * Deliberately has no `data/use*Mutation.ts` hook above it, unlike every SharePoint operation in this
 * app. Every file in the file manager's `data/` directory exists to invalidate directory query keys
 * after changing the file system — and creating a mail draft changes nothing in SharePoint, so there is
 * no key to invalidate. A mutation hook would assert a cache relationship that does not exist, and
 * vue-query's `isPending`/`error` are useless against a multi-phase per-file progress model anyway.
 * That is a choice, not an oversight.
 *
 * Every method acquires its own token, matching sharePointDriveService's convention. The interactive
 * consent still happens exactly once, inside the click gesture, because the caller acquires the same
 * scope up front and MSAL's cache serves every request after that silently.
 */
class GraphMailService {
  /**
   * Creates an empty draft in the signed-in user's mailbox and returns its id and web link.
   *
   * Subject, body and recipients are ALL left blank on purpose. The reason for sending is business
   * context this app does not have; a folder-derived subject would be a guess the user has to clear
   * first. Outlook owns the address book, the groups and the autocomplete, and the first field its
   * cursor lands in is Subject.
   *
   * `webLink` is a default property of the message resource, so no follow-up GET is needed. It opens
   * Outlook on the web; the draft still syncs to a desktop client within seconds.
   */
  async createDraft(signal?: AbortSignal): Promise<MailDraft> {
    const token = await acquireMailToken();
    const endpoint = `${GRAPH_BASE_URL}/me/messages`;

    const message: GraphMessage = await withRetry(
      async () => {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          // An empty body is a valid draft. isDraft is implied by POSTing to /me/messages.
          body: JSON.stringify({}),
          ...(signal ? { signal } : {}),
        });

        if (!response.ok) {
          logTokenOn401(response, token);
          throw await graphErrorWithDetail(
            'Failed to create Outlook draft',
            response,
          );
        }

        return (await response.json()) as GraphMessage;
      },
      { ...GRAPH_RETRY_OPTIONS, signal },
    );

    if (!message.id || !message.webLink) {
      throw new Error('Outlook draft was created without an id or web link');
    }

    const webLink = toComposeWebLink(message.webLink, message.id);

    return {
      id: message.id,
      webLink,
    };
  }

  /**
   * Attaches a blob inline, as base64 in the request body. For blobs at or under
   * INLINE_ATTACHMENT_MAX_BYTES only — see attachLarge for the rest.
   *
   * `contentType` is passed through from the download response rather than defaulted, because without
   * it Outlook labels everything `application/octet-stream` and OWA then refuses to preview even a PDF
   * inline.
   */
  async attachSmall(
    messageId: string,
    name: string,
    contentType: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await acquireMailToken();
    const endpoint = `${GRAPH_BASE_URL}/me/messages/${messageId}/attachments`;
    const contentBytes = await toBase64(blob);

    await withRetry(
      async () => {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            '@odata.type': '#microsoft.graph.fileAttachment',
            name,
            contentType,
            contentBytes,
          }),
          ...(signal ? { signal } : {}),
        });

        if (!response.ok) {
          logTokenOn401(response, token);
          throw await graphErrorWithDetail(
            'Failed to attach Outlook file',
            response,
          );
        }
      },
      { ...GRAPH_RETRY_OPTIONS, signal },
    );
  }

  /**
   * Attaches a blob through an upload session, chunked. For blobs above INLINE_ATTACHMENT_MAX_BYTES.
   *
   * Returns the attachment's `Location`, when the upload host exposed that header — informational only.
   * Deliberately NOT thrown on when absent: the upload host is a different origin and need not list
   * `Location` in `Access-Control-Expose-Headers`, and reporting a successful attachment as failed
   * because a header was hidden is exactly the class of bug per-file failure reporting exists to avoid.
   * A non-ok status is the real failure signal, and uploadInChunks has already raised it by then.
   */
  async attachLarge(
    messageId: string,
    name: string,
    contentType: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<string | null> {
    const token = await acquireMailToken();
    const sessionEndpoint = `${GRAPH_BASE_URL}/me/messages/${messageId}/attachments/createUploadSession`;

    const session: GraphUploadSession = await withRetry(
      async () => {
        const response = await fetch(sessionEndpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          // `size` is declared up front here, unlike the drive's upload session — Outlook validates the
          // byte range against it and rejects a session declaring size 0 outright, which is why
          // uploadInChunks' zero-byte branch is unreachable from this path.
          body: JSON.stringify({
            AttachmentItem: {
              attachmentType: 'file',
              name,
              contentType,
              size: blob.size,
            },
          }),
          ...(signal ? { signal } : {}),
        });

        if (!response.ok) {
          logTokenOn401(response, token);
          throw await graphErrorWithDetail(
            'Failed to create Outlook attachment upload session',
            response,
          );
        }

        return (await response.json()) as GraphUploadSession;
      },
      { ...GRAPH_RETRY_OPTIONS, signal },
    );

    if (!session.uploadUrl) {
      throw new Error(
        'Outlook attachment upload session returned no upload URL',
      );
    }

    const response = await uploadInChunks(session.uploadUrl, blob, {
      chunkSize: ATTACHMENT_CHUNK_SIZE_BYTES,
      signal,
      errorMessage: 'Failed to upload Outlook attachment chunk',
    });

    // The final PUT answers 201 with an EMPTY body — parsing it would throw SyntaxError, which is why
    // uploadInChunks hands back the unread Response instead of a parsed item.
    return response.headers?.get?.('Location') ?? null;
  }

  /**
   * Deletes a draft. Used by the cancel path only, to undo a half-built draft the user explicitly
   * abandoned — litter in a real mailbox otherwise.
   *
   * Takes NO signal, on purpose and load-bearingly: this runs *because* the operation's signal was
   * aborted, so passing it would abort the cleanup instantly and leave behind exactly the orphan draft
   * this method exists to prevent.
   *
   * A 404 counts as success — the draft is gone either way, and during teardown a lost race is not an
   * error worth reporting.
   */
  async deleteDraft(messageId: string): Promise<void> {
    const token = await acquireMailToken();
    const endpoint = `${GRAPH_BASE_URL}/me/messages/${messageId}`;

    await withRetry(async () => {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok && response.status !== 404) {
        logTokenOn401(response, token);
        throw await graphErrorWithDetail(
          'Failed to delete Outlook draft',
          response,
        );
      }
    }, GRAPH_RETRY_OPTIONS);
  }
}

export const graphMailService = new GraphMailService();
